import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import ClientContactCard from './ClientContactCard';
import {
  Lightning, LockSimple, MapPin, CalendarBlank, CurrencyEur, FirstAid,
  Note, User, Tag, Coins, Clock,
} from '@phosphor-icons/react';

const STATUS_LABEL = {
  pending: 'In attesa di invio', notified: 'Nuova richiesta', pending_pro: 'In attesa di risposta',
  pending_date: 'In attesa della data', accepted: 'Accettata', in_progress: 'In corso',
  completed: 'Completata', reviewed: 'Recensita', cancelled: 'Annullata',
  closed_by_pro: 'Chiusa dal professionista',
};
const URGENCY_LABEL = { low: 'Non urgente', medium: 'Entro pochi giorni', high: 'Urgente' };

const fmtDate = (iso, withTime = false) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-2 py-2 border-b border-border/40 last:border-0" data-testid={testId}>
    <Icon size={15} className="text-primary mt-0.5 shrink-0" weight="duotone" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-foreground/50">{label}</div>
      <div className="text-sm text-foreground/90 break-words">{children}</div>
    </div>
  </div>
);

export const RequestDetailDialog = ({ request, open, onOpenChange }) => {
  if (!request) return null;
  const r = request;
  const locked = r.contact_locked ?? true;
  const zone = [r.comune, r.province, r.region].filter(Boolean).join(' · ') || r.location;
  const needs = r.needs_labels || r.needs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
            Dettaglio richiesta
            <Badge className="bg-primary/10 text-primary border-0">{STATUS_LABEL[r.status] || r.status}</Badge>
            {r.is_urgent && (
              <Badge className="bg-red-100 text-red-800 border-0">
                <Lightning size={11} weight="fill" className="mr-1" /> URGENTE
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-lg font-heading font-medium" data-testid="detail-service">
              {r.service_type || r.need_category || 'Prestazione'}
            </div>
            {(r.profession_codes || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="detail-professions">
                {r.profession_codes.map((p) => (
                  <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                ))}
              </div>
            )}
          </div>

          {needs.length > 0 && (
            <div data-testid="detail-needs">
              <div className="text-[11px] uppercase tracking-wider text-foreground/50 mb-1">Bisogni indicati dal cliente</div>
              <div className="flex flex-wrap gap-1">
                {needs.map((n, i) => <Badge key={i} className="bg-sky-50 text-sky-800 border-0">{n}</Badge>)}
              </div>
            </div>
          )}

          {r.health_conditions && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200" data-testid="detail-health">
              <div className="flex items-center gap-2 text-sm font-semibold text-rose-900 mb-1">
                <FirstAid size={16} weight="duotone" /> Patologie o condizioni particolari
              </div>
              <p className="text-sm text-rose-900 whitespace-pre-wrap">{r.health_conditions}</p>
            </div>
          )}

          {r.description && (
            <div className="p-3 rounded-xl bg-muted/40" data-testid="detail-description">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1">
                <Note size={16} weight="duotone" className="text-primary" /> Note del cliente
              </div>
              <p className="text-sm text-foreground/85 whitespace-pre-wrap">{r.description}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-x-6">
            <Row icon={MapPin} label="Zona" testId="detail-zone">{zone || 'Non indicata'}</Row>
            <Row icon={Clock} label="Urgenza" testId="detail-urgency">
              {URGENCY_LABEL[r.urgency] || r.urgency || 'Non indicata'}
              {r.urgent_surcharge_eur ? ` · supplemento €${r.urgent_surcharge_eur}` : ''}
            </Row>
            <Row icon={CurrencyEur} label="Budget indicativo" testId="detail-budget">
              {r.budget ? `€${r.budget}${r.budget_type === 'monthly' ? '/mese' : r.budget_type === 'from' ? ' (a partire da)' : ''}` : 'Non indicato'}
            </Row>
            <Row icon={CalendarBlank} label="Periodo richiesto" testId="detail-period">
              {r.start_date || r.end_date
                ? `${fmtDate(r.start_date) || '—'} → ${fmtDate(r.end_date) || '—'}`
                : 'Da concordare'}
            </Row>
            <Row icon={CalendarBlank} label="Data della prestazione" testId="detail-appointment">
              {r.appointment_at ? fmtDate(r.appointment_at, true)
                : r.proposed_appointment_at ? `Proposta: ${fmtDate(r.proposed_appointment_at, true)}`
                  : 'Non ancora fissata'}
            </Row>
            <Row icon={Clock} label="Richiesta creata il" testId="detail-created">
              {fmtDate(r.created_at, true) || '—'}
            </Row>
            {(r.credits_cost || r.credits_hold_amount) && (
              <Row icon={Coins} label="Crediti" testId="detail-credits">
                {r.credits_cost
                  ? `${r.credits_cost} crediti addebitati`
                  : `${r.credits_hold_amount} crediti riservati (addebitati a prestazione conclusa)`}
              </Row>
            )}
            {r.promotion_id && (
              <Row icon={Tag} label="Promozione applicata" testId="detail-promotion">
                {r.promotion_title || r.promotion_id}
              </Row>
            )}
            {r.assigned_internal_name && (
              <Row icon={User} label="Operatore del Centro" testId="detail-internal">
                {r.assigned_internal_name}
                {r.internal_assignment_status === 'pending' ? ' · in attesa di conferma' : ''}
              </Row>
            )}
          </div>

          {locked ? (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="detail-locked">
              <LockSimple size={16} className="text-amber-700 mt-0.5" />
              <div className="text-xs text-amber-900">
                <strong>Dati di contatto riservati.</strong> Nomi, telefoni, email e indirizzo del
                cliente sono visibili a chi accetta la richiesta.
              </div>
            </div>
          ) : (
            <ClientContactCard c={r} testId="detail-contacts" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestDetailDialog;
